import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ScorecardModule } from '@galaxy/snapshot';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SubscriptionList } from '@vendasta/rx-utils';
import { BehaviorSubject, firstValueFrom, Observable, take } from 'rxjs';
import { getImageSrc } from '../../../core/images';
import { SnapshotService } from '../../../core/snapshot.service';
import { SnapshotSidepanelComponentInputs, SnapshotSidepanelService } from './snapshot-sidepanel.service';

export interface CreateSnapshotParams {
  partnerId: string;
  origin: string;
  accountGroupId: string;
  companyId: string;
}

@Component({
  selector: 'app-snapshot-sidepanel',
  imports: [
    GalaxyEmptyStateModule,
    MatToolbarModule,
    MatButtonModule,
    CommonModule,
    TranslateModule,
    ScorecardModule,
    GalaxyLoadingSpinnerModule,
  ],
  templateUrl: './snapshot-sidepanel.component.html',
  styleUrls: ['./snapshot-sidepanel.component.scss'],
})
export class SnapshotSidepanelComponent implements OnDestroy {
  companyId: string;
  partnerId?: string;
  trackingCategory?: string;
  snapshotName?: string;
  emptyStateImage = getImageSrc('images/empty-state/crm-snapshot-empty-state.svg');

  accountGroupId$: Observable<string>;
  protected snapshotId$: Observable<string>;
  public loading$: Observable<boolean>;

  private readonly createButtonLoading$$ = new BehaviorSubject<boolean>(false);
  public createButtonLoading$ = this.createButtonLoading$$.asObservable();

  private readonly subscriptions = SubscriptionList.new();
  hasEditAccess$: Observable<boolean>;

  constructor(
    private readonly snapshotSidepanelService: SnapshotSidepanelService,
    private readonly snapshotService: SnapshotService,
  ) {
    this.accountGroupId$ = this.snapshotSidepanelService.accountGroupId$;
    this.hasEditAccess$ = this.snapshotService.partnerHasAccessToSnapshotEditing();
    this.snapshotId$ = this.snapshotSidepanelService.currentSnapshotId$;
    this.loading$ = this.snapshotSidepanelService.currentSnapshotIsLoading$;
  }

  public setInputs(inputs: SnapshotSidepanelComponentInputs) {
    this.companyId = inputs.companyID;
    this.partnerId = inputs.partnerID;
    this.snapshotSidepanelService.setAccountGroupId(inputs.accountGroupID);
    this.trackingCategory = inputs.trackingCategory;
    this.snapshotName = inputs.snapshotName;
    this.snapshotSidepanelService.loadCurrentSnapshot(inputs.partnerID, inputs.companyID);
  }

  public async createSnapshotClicked() {
    this.createButtonLoading$$.next(true);
    const accountGroupId = await firstValueFrom(this.accountGroupId$);
    const createSnapshotRequest: CreateSnapshotParams = {
      partnerId: this.partnerId,
      origin: this.trackingCategory,
      accountGroupId: accountGroupId,
      companyId: this.companyId,
    };

    const work$ = this.snapshotSidepanelService.createSnapshot(createSnapshotRequest);
    work$.pipe(take(1)).subscribe({
      next: (snapshotId) => {
        if (snapshotId) {
          this.snapshotService.showCreateSnapshotSuccessMessage();
        }
        this.createButtonLoading$$.next(false);
      },
      error: () => {
        this.snapshotService.showCreateSnapshotErrorMessage();
        this.createButtonLoading$$.next(false);
      },
    });
  }

  public async refreshSnapshotClicked() {
    const accountGroupId = await firstValueFrom(this.accountGroupId$);
    const work$ = accountGroupId
      ? this.snapshotSidepanelService.refreshSnapshotByAccountGroupId(
          accountGroupId,
          this.partnerId,
          this.trackingCategory,
        )
      : this.snapshotSidepanelService.refreshSnapshotByCompanyId(this.companyId, this.partnerId, this.trackingCategory);

    work$.pipe(take(1)).subscribe({
      next: (hasRefreshed) => {
        if (hasRefreshed) {
          this.snapshotService.showRefreshSnapshotSuccessMessage();
        }
      },
      error: () => {
        this.snapshotService.showRefreshSnapshotErrorMessage();
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }
}
